import BubbleCommon from '@op/components/BubbleCommon';
import { channelInfo } from '@op/components/ChannelLabel';
import { NO_DATE } from '@op/constants';
import ProgressColumn from '@op/pages/Overview/CompareKeyMetrics/component/ProgressColumn';
import TableColumn from '@op/pages/Overview/CompareKeyMetrics/component/TableColumn';
import TableHeader from '@op/pages/Overview/CompareKeyMetrics/component/TableHeader';
import {
  CommunityOptionsType,
  compareKeyMetricsTable,
  CompareKeyMetricsTableOneKey,
  GameStoreOneOptionsType,
  GameStoreTwoOptionsType,
  mentionsSentimentFormatFuncMap,
  mentionsSentimentRenderHelperFuncMap,
  MentionsSentimentSubSortType,
  MentionsSentimentTabType,
  OverallColumnLabel,
  SteamOptionsType,
  TableColumnsFuncType,
} from '@op/pages/Overview/CompareKeyMetrics/constants';
import { getGameColorOrName } from '@op/pages/Overview/CompareKeyMetrics/utils';
import { CommonColumnsType } from '@op/types/table';
import { transLanguage } from '@op/utils';
import { formatDateFunc } from '@op/utils/time';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'umi';

export function getBaseList(baseColumn: Array<string>, type: any, tab: string) {
  const columns = baseColumn.map((item: any) => {
    const isChannel = type === 'by_channel';
    let label = '';
    if (isChannel) {
      const { name } = channelInfo(item);
      label = name;
    } else {
      label = transLanguage(label || item);
    }
    if (item === 'overall') {
      label = transLanguage(OverallColumnLabel[tab]);
    }
    return {
      header: label || item,
      key: item,
      align: 'right',
      format: mentionsSentimentFormatFuncMap[tab],
      ...mentionsSentimentRenderHelperFuncMap[tab],
    };
  });
  const overallIndex = columns.findIndex((col) => col.key === 'overall');
  if (overallIndex > -1) {
    const [overallColumn] = columns.splice(overallIndex, 1);
    columns.splice(0, 0, overallColumn);
  }
  return columns;
}

// 默认方法不处理动态列的情况
export function defaultDealwithTableColumns(
  columns?: Array<CommonColumnsType> | TableColumnsFuncType,
  baseColumn: Array<{ header: any; key: string }> = [],
) {
  if (!columns) return [];
  if (typeof columns === 'function') return columns(baseColumn);
  return columns;
}

export function getGameDataIndex(gameData: any) {
  if (!gameData || typeof gameData !== 'object') return {};
  const result: any = {};
  for (const key in gameData) {
    const element = gameData[key];
    result[key] = element.index;
  }

  return result;
}

export function getKeyColumns(
  oneKey: CompareKeyMetricsTableOneKey,
  twoKey?:
    | MentionsSentimentTabType
    | GameStoreOneOptionsType
    | SteamOptionsType
    | CommunityOptionsType,
  threeKey?: MentionsSentimentSubSortType | GameStoreTwoOptionsType,
): Array<CommonColumnsType> | TableColumnsFuncType | undefined {
  const oneColumnTable = compareKeyMetricsTable[oneKey];
  // 只有一级的情况
  if (Array.isArray(oneColumnTable)) return oneColumnTable;
  const twoColumnTable = oneColumnTable[twoKey];
  // 只有二级的情况
  if (Array.isArray(twoColumnTable)) return twoColumnTable;
  // 处理三级的情况
  const threeColumnTable = twoColumnTable?.[threeKey];

  return threeColumnTable;
}

export default function useCompareKeyMetricsColumns({
  oneKey,
  twoKey,
  threeKey,
  isAuto = true,
  gameData,
  tableRecords,
  filter,
  MergeChannelIconElement,
}: {
  oneKey: CompareKeyMetricsTableOneKey;
  twoKey?:
    | MentionsSentimentTabType
    | GameStoreOneOptionsType
    | SteamOptionsType
    | CommunityOptionsType;
  threeKey?: MentionsSentimentSubSortType | GameStoreTwoOptionsType;
  isAuto?: boolean;
  gameData?: any;
  tableRecords?: any;
  filter?: any;
  MergeChannelIconElement?: any;
}) {
  const [columns, setColumns] = useState<Array<CommonColumnsType>>([]);
  const [sortColumns, setSortColumns] = useState<Array<string>>([]);
  const getGameData = getGameColorOrName(gameData);
  const { formatMessage } = useIntl();
  function setOriginColumns(
    baseColumn: Array<{ header: any; key: string }> = [],
    total?: number,
  ) {
    const originColumns = defaultDealwithTableColumns(
      getKeyColumns(oneKey, twoKey, threeKey),
      baseColumn,
    );
    const showText = total === undefined ? tableRecords?.length : total;
    const columns: Array<CommonColumnsType> = [
      {
        key: 'game',
        width: 326,
        header: (
          <TableHeader
            title={formatMessage({ id: 'Game' })}
            subtitle={showText ? `(${showText})` : ''}
          />
        ),
        render: (cvm: any) => {
          const item: any = getGameData(cvm?.game, 'all');
          return (
            <TableColumn
              isCurrentGame={
                item.edition_unified_id === filter.edition_unified_id
              }
              name={item?.entity_name}
              item={item}
            />
          );
        },
      },
    ];
    const sortColumns: Array<string> = [];
    const renderNoData = ({ data, key }: { data: any; key: string }) => {
      const item: any = getGameData(data?.game, 'all');
      const { release_time: releaseTime }: any = item || {};
      const formatDate = (date: moment.MomentInput) =>
        moment(date, 'YYYY-MM-DD');

      if (
        [
          'recent_reviews',
          'recent_reviews_recommend',
          'recent_reviews_non_recommend',
        ].includes(key) &&
        !data?.[key]
      ) {
        if (
          !releaseTime ||
          formatDate(data.end_time).diff(formatDate(releaseTime), 'days') < 30
        ) {
          return (
            <div className="dbo-table-no-data">
              {formatMessage({
                id: 'Data available on the 31st day after launch',
              })}
            </div>
          );
        }
      }

      if (
        [
          'all_reviews',
          'all_reviews_recommend',
          'all_reviews_non_recommend',
        ].includes(key) &&
        !data?.[key]
      ) {
        if (
          !releaseTime ||
          formatDate(data.end_time).isBefore(formatDate(releaseTime), 'days')
        ) {
          return (
            <div className="dbo-table-no-data">
              {formatMessage({
                id: 'Data available after launch',
              })}
            </div>
          );
        }
      }

      return null;
    };

    originColumns.forEach(
      ({ tips, key, header, progress, format, align = 'right', ...res }) => {
        const text = formatMessage({ id: header || NO_DATE.EMPTY });
        columns.push({
          header: (
            <BubbleCommon
              placement="top"
              tipsMap={tips}
              showIcon={false}
              overlayClassName="dbo-ul-ol-bubble-tips"
              extraContent={
                MergeChannelIconElement && (
                  <MergeChannelIconElement
                    metric={key}
                  ></MergeChannelIconElement>
                )
              }
            >
              {text}
            </BubbleCommon>
          ),
          text: header,
          key,
          align,
          format,
          width: 216,
          ...res,
          render: (cvm: any) => {
            const noData = renderNoData({
              data: {
                ...cvm,
                start_time: filter?.start_time,
                end_time: filter?.end_time,
              },
              key,
            });
            if (noData !== null) {
              return noData;
            }
            return (
              <ProgressColumn
                percentage={cvm[`${key}_ratio`]}
                value={cvm[key]}
                showProgress={progress}
                emptyValue={'-'}
                hideTrend={true}
                totalValue={cvm[`${key}_total`]}
                oldTime={cvm?.oldTime || []}
                newTime={cvm?.newTime || []}
                oldValue={cvm[`${key}_old`]}
                formateValue={format}
                formateDate={(value) =>
                  formatDateFunc(filter?.date_type)(value)
                }
              />
            );
          },
        });
        sortColumns.push(key);
      },
    );

    setColumns(columns);
    setSortColumns(sortColumns);
  }

  useEffect(() => {
    if (isAuto) setOriginColumns();
  }, [twoKey, threeKey, tableRecords, formatMessage]);

  return { columns, sortColumns, setOriginColumns };
}
